import React, { type JSX } from 'react';

type TagOrComponent = keyof JSX.IntrinsicElements | React.ComponentType<any>;

/**
 * MUI v4's `styled` forwards **every** prop to the underlying DOM node — it has no
 * `shouldForwardProp`. So style-only props (`isvalid`, `iserror`, `isOpen`,
 * `isExpanded`, `tone`, `variant`, `customwidth`…) leak onto the element as invalid
 * DOM attributes: some trigger React's "unknown attribute" / "non-boolean attribute"
 * console warnings, the rest render as stray attributes.
 *
 * Wrap the target with `domTag('input', ['isvalid', …])` so the named props are still
 * delivered to the style creator (for computing styles) but stripped before they reach
 * the DOM. Works for intrinsic tags and for wrapped components (Link, Typography, …).
 *
 *   export const TextInput = styled(domTag('input', ['iserror', 'isvalid']))(({ isvalid }) => ({ … }));
 */
export const domTag = (tag: TagOrComponent, omit: readonly string[]) => {
	const Filtered = React.forwardRef<unknown, Record<string, unknown>>((props, ref) => {
		const clean: Record<string, unknown> = {};
		Object.keys(props).forEach((key) => {
			if (!omit.includes(key)) clean[key] = props[key];
		});
		return React.createElement(tag as React.ElementType, { ...clean, ref });
	});
	Filtered.displayName = `domTag(${typeof tag === 'string' ? tag : 'Component'})`;
	return Filtered;
};
